/**
 * Utility functions for handling RPE (Rate of Perceived Exertion) values
 */

/**
 * Parse RPE string from exercises.json format (e.g., "~7", "~7-8") and return numeric value(s)
 */
export const parseRPE = (rpeString: string): { min: number; max: number } => {
  // Remove the ~ symbol
  const cleanRpe = rpeString.replace('~', '');
  
  // Check if it's a range (e.g., "7-8")
  if (cleanRpe.includes('-')) {
    const [min, max] = cleanRpe.split('-').map(num => parseFloat(num));
    return { min, max };
  } else {
    // Single value (e.g., "7")
    const value = parseFloat(cleanRpe);
    return { min: value, max: value };
  }
};

/**
 * Get RPE description based on the RPE value
 */
export const getRPEDescription = (rpeString: string): string => {
  const { min, max } = parseRPE(rpeString);
  
  // For ranges, use the average to determine description
  const avgRpe = (min + max) / 2;
  
  if (avgRpe >= 10) {
    return "You reached failure. You actually tried and failed to get the weight all the way up";
  } else if (avgRpe >= 9.5) {
    return "You didn't actually reach failure, but you were very close. You might have been able to add a little more weight, but you definitely didn't leave more than 1 rep in the tank";
  } else if (avgRpe >= 9) {
    return "You could've done one more rep if you really tried. This is still a hard set";
  } else if (avgRpe >= 8.5) {
    return "You could've done 1 or 2 more reps if you really tried. It wasn't a brutal set, but you're still pretty close to failure here";
  } else if (avgRpe >= 8) {
    return "You probably had 2 reps left in the tank. It wasn't a super hard set, but will still certainly stimulate hypertrophy";
  } else if (avgRpe >= 7.5) {
    return "There are 2 or 3 reps in the tank here. Depending on the exercise, this is usually not going to feel like a truly hard set";
  } else if (avgRpe >= 7) {
    return "You have 3 reps left in the tank. On most exercises, this would feel like something between a tough warm up set and an easy working set";
  } else if (avgRpe >= 6.5) {
    return "You have 3 or maybe 4 reps left in the tank";
  } else if (avgRpe >= 6) {
    return "You could've done 4 more if you really tried";
  } else if (avgRpe >= 5) {
    return "You could've done 5 more if you really tried";
  } else if (avgRpe >= 1) {
    return "More than 5 reps left in the tank. These are warm-up sets, not working sets";
  } else {
    return "Rate of Perceived Exertion - how hard the set felt";
  }
};

/**
 * Format RPE for display (e.g., "~7-8" -> "7-8 RPE")
 */
export const formatRPEForDisplay = (rpeString: string): string => {
  const cleanRpe = rpeString.replace('~', '');
  return `${cleanRpe} RPE`;
}; 